/**
 * LanguageContext
 *
 * React Context for internationalization (i18n):
 * - Manages current language state (EN, DE, PL, DA, UK, CS, TR, AR, NL, ES, FR)
 * - Provides translation function `t(key)`
 * - Persists language preference to localStorage
 * - Triggers component re-renders on language change
 *
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import type { LanguageCode, FieldLabels } from '../types/method-fields';

/**
 * UI Translation keys - all translatable UI strings
 */
export interface UITranslations {
  // Header
  language: string;

  // Filters
  filters: string;
  module: string;
  topic: string;
  activityType: string;
  allModules: string;
  allTopics: string;
  allActivityTypes: string;
  duration: string;
  durationMinutes: string;
  min: string;
  max: string;
  resetFilters: string;
  showMore: string;
  showLess: string;
  showFilters: string;
  loadingFilters: string;

  // Learning Plan
  learningPlan: string;
  planEmpty: string;
  planEmptyHint: string;
  totalDuration: string;
  minutes: string;
  method: string;
  methods: string;
  exportPdf: string;
  clearPlan: string;

  // Method List
  loadingMethods: string;
  noMethodsFound: string;
  tryAdjustingFilters: string;

  // Method Modal
  loading: string;
  loadingMethodDetails: string;
  addToPlan: string;
  downloadPdf: string;
  downloadMaterials: string;
  closeModal: string;

  // Errors
  error: string;

  // InfoCard
  infocard_title: string;
  infocard_body: string;
  infocard_dismiss: string;
}

/**
 * Complete translations type (UI + Field Labels)
 */
export interface Translations extends UITranslations {
  fieldLabels: FieldLabels;
}

/**
 * All translations for supported languages
 */
const translations: Record<LanguageCode, Translations> = {
  en: {
    // Header
    language: 'Language',

    // Filters
    filters: 'Filters',
    module: 'Module',
    topic: 'Topic',
    activityType: 'Activity Type',
    allModules: 'All Modules',
    allTopics: 'All Topics',
    allActivityTypes: 'All Activity Types',
    duration: 'Duration',
    durationMinutes: 'Duration (minutes)',
    min: 'Min',
    max: 'Max',
    resetFilters: 'Reset Filters',
    showMore: 'more',
    showLess: 'Show less',
    showFilters: 'Show filters',
    loadingFilters: 'Loading filters...',

    // Learning Plan
    learningPlan: 'Learning Plan',
    planEmpty: 'Your plan is empty.',
    planEmptyHint: 'Add methods from the catalog to get started.',
    totalDuration: 'Total Duration',
    minutes: 'minutes',
    method: 'method',
    methods: 'methods',
    exportPdf: 'Export PDF',
    clearPlan: 'Clear Plan',

    // Method List
    loadingMethods: 'Loading methods...',
    noMethodsFound: 'No methods found matching your filters.',
    tryAdjustingFilters: 'Try adjusting your search criteria.',

    // Method Modal
    loading: 'Loading...',
    loadingMethodDetails: 'Loading method details...',
    addToPlan: 'Add to Plan',
    downloadPdf: 'Download Scenario',
    downloadMaterials: 'Download Attachments',
    closeModal: 'Close modal',

    // Errors
    error: 'Error',

    // InfoCard
    infocard_title: 'How to use this tool',
    infocard_body: 'Browse and filter learning methods, then add them to your training plan. Export the plan as a PDF to use in your session.',
    infocard_dismiss: 'Got it',

    // Field Labels (for method detail sections)
    fieldLabels: {
      title: 'Title',
      duration: 'Duration',
      description: 'Description',
      module: 'Module',
      topics: 'Topics',
      activity_types: 'Activity Types',
      contributor: 'Contributor',
      learning_goals: 'Learning Goals',
      preparation: 'Preparation',
      materials: 'Materials',
      resources: 'Resources',
      download_materials: 'Download Attachments',
    },
  },

  de: {
    // Header
    language: 'Sprache',

    // Filters
    filters: 'Filter',
    module: 'Modul',
    topic: 'Thema',
    activityType: 'Aktivitätstyp',
    allModules: 'Alle Module',
    allTopics: 'Alle Themen',
    allActivityTypes: 'Alle Aktivitätstypen',
    duration: 'Dauer',
    durationMinutes: 'Dauer (Minuten)',
    min: 'Min',
    max: 'Max',
    resetFilters: 'Filter zurücksetzen',
    showMore: 'mehr',
    showLess: 'Weniger anzeigen',
    showFilters: 'Filter anzeigen',
    loadingFilters: 'Filter werden geladen...',

    // Learning Plan
    learningPlan: 'Lernplan',
    planEmpty: 'Dein Plan ist leer.',
    planEmptyHint: 'Füge Methoden aus dem Katalog hinzu, um zu beginnen.',
    totalDuration: 'Gesamtdauer',
    minutes: 'Minuten',
    method: 'Methode',
    methods: 'Methoden',
    exportPdf: 'PDF exportieren',
    clearPlan: 'Plan leeren',

    // Method List
    loadingMethods: 'Methoden werden geladen...',
    noMethodsFound: 'Keine Methoden gefunden, die deinen Filtern entsprechen.',
    tryAdjustingFilters: 'Versuche, deine Suchkriterien anzupassen.',

    // Method Modal
    loading: 'Wird geladen...',
    loadingMethodDetails: 'Methodendetails werden geladen...',
    addToPlan: 'Zum Plan hinzufügen',
    downloadPdf: 'Szenario herunterladen',
    downloadMaterials: 'Anhänge herunterladen',
    closeModal: 'Modal schließen',

    // Errors
    error: 'Fehler',

    // InfoCard
    infocard_title: 'So nutzen Sie dieses Tool',
    infocard_body: 'Durchsuchen und filtern Sie Lernmethoden und fügen Sie sie Ihrem Trainingsplan hinzu. Exportieren Sie den Plan als PDF für Ihre Sitzung.',
    infocard_dismiss: 'Verstanden',

    // Field Labels
    fieldLabels: {
      title: 'Titel',
      duration: 'Dauer',
      description: 'Beschreibung',
      module: 'Modul',
      topics: 'Themen',
      activity_types: 'Aktivitätstypen',
      contributor: 'Mitwirkende',
      learning_goals: 'Lernziele',
      preparation: 'Vorbereitung',
      materials: 'Benötigte Materialien',
      resources: 'Online-Ressourcen',
      download_materials: 'Anhänge herunterladen',
    },
  },

  pl: {
    // Header
    language: 'Język',

    // Filters
    filters: 'Filtry',
    module: 'Moduł',
    topic: 'Temat',
    activityType: 'Typ aktywności',
    allModules: 'Wszystkie moduły',
    allTopics: 'Wszystkie tematy',
    allActivityTypes: 'Wszystkie typy aktywności',
    duration: 'Czas trwania',
    durationMinutes: 'Czas trwania (minuty)',
    min: 'Min',
    max: 'Maks',
    resetFilters: 'Resetuj filtry',
    showMore: 'więcej',
    showLess: 'Pokaż mniej',
    showFilters: 'Pokaż filtry',
    loadingFilters: 'Ładowanie filtrów...',

    // Learning Plan
    learningPlan: 'Plan nauki',
    planEmpty: 'Twój plan jest pusty.',
    planEmptyHint: 'Dodaj metody z katalogu, aby zacząć.',
    totalDuration: 'Całkowity czas',
    minutes: 'minut',
    method: 'metoda',
    methods: 'metody',
    exportPdf: 'Eksportuj PDF',
    clearPlan: 'Wyczyść plan',

    // Method List
    loadingMethods: 'Ładowanie metod...',
    noMethodsFound: 'Nie znaleziono metod pasujących do filtrów.',
    tryAdjustingFilters: 'Spróbuj dostosować kryteria wyszukiwania.',

    // Method Modal
    loading: 'Ładowanie...',
    loadingMethodDetails: 'Ładowanie szczegółów metody...',
    addToPlan: 'Dodaj do planu',
    downloadPdf: 'Pobierz scenariusz',
    downloadMaterials: 'Pobierz załączniki',
    closeModal: 'Zamknij okno',

    // Errors
    error: 'Błąd',

    // InfoCard
    infocard_title: 'Jak korzystać z tego narzędzia',
    infocard_body: 'Przeglądaj i filtruj metody uczenia się, a następnie dodaj je do swojego planu szkolenia. Eksportuj plan jako PDF do wykorzystania na sesji.',
    infocard_dismiss: 'Rozumiem',

    // Field Labels
    fieldLabels: {
      title: 'Tytuł',
      duration: 'Czas trwania',
      description: 'Opis',
      module: 'Moduł',
      topics: 'Tematy',
      activity_types: 'Typy aktywności',
      contributor: 'Współautor',
      learning_goals: 'Cele uczenia się',
      preparation: 'Przygotowanie',
      materials: 'Wymagane materiały',
      resources: 'Zasoby online',
      download_materials: 'Pobierz załączniki',
    },
  },

  da: {
    // Header
    language: 'Sprog',

    // Filters
    filters: 'Filtre',
    module: 'Modul',
    topic: 'Emne',
    activityType: 'Aktivitetstype',
    allModules: 'Alle moduler',
    allTopics: 'Alle emner',
    allActivityTypes: 'Alle aktivitetstyper',
    duration: 'Varighed',
    durationMinutes: 'Varighed (minutter)',
    min: 'Min',
    max: 'Maks',
    resetFilters: 'Nulstil filtre',
    showMore: 'mere',
    showLess: 'Vis mindre',
    showFilters: 'Vis filtre',
    loadingFilters: 'Indlæser filtre...',

    // Learning Plan
    learningPlan: 'Læringsplan',
    planEmpty: 'Din plan er tom.',
    planEmptyHint: 'Tilføj metoder fra kataloget for at komme i gang.',
    totalDuration: 'Samlet varighed',
    minutes: 'minutter',
    method: 'metode',
    methods: 'metoder',
    exportPdf: 'Eksporter PDF',
    clearPlan: 'Ryd plan',

    // Method List
    loadingMethods: 'Indlæser metoder...',
    noMethodsFound: 'Ingen metoder fundet, der matcher dine filtre.',
    tryAdjustingFilters: 'Prøv at justere dine søgekriterier.',

    // Method Modal
    loading: 'Indlæser...',
    loadingMethodDetails: 'Indlæser metodedetaljer...',
    addToPlan: 'Tilføj til plan',
    downloadPdf: 'Download scenarie',
    downloadMaterials: 'Download vedhæftede filer',
    closeModal: 'Luk vindue',

    // Errors
    error: 'Fejl',

    // InfoCard
    infocard_title: 'Sådan bruger du dette værktøj',
    infocard_body: 'Gennemse og filtrer læringsmetoder, og tilføj dem til din træningsplan. Eksporter planen som PDF til brug i din session.',
    infocard_dismiss: 'Forstået',

    // Field Labels
    fieldLabels: {
      title: 'Titel',
      duration: 'Varighed',
      description: 'Beskrivelse',
      module: 'Modul',
      topics: 'Emner',
      activity_types: 'Aktivitetstyper',
      contributor: 'Bidragyder',
      learning_goals: 'Læringsmål',
      preparation: 'Forberedelse',
      materials: 'Nødvendige materialer',
      resources: 'Online-ressourcer',
      download_materials: 'Download vedhæftede filer',
    },
  },

  uk: {
    // Header
    language: 'Мова',

    // Filters
    filters: 'Фільтри',
    module: 'Модуль',
    topic: 'Тема',
    activityType: 'Тип діяльності',
    allModules: 'Усі модулі',
    allTopics: 'Усі теми',
    allActivityTypes: 'Усі типи діяльності',
    duration: 'Тривалість',
    durationMinutes: 'Тривалість (хвилини)',
    min: 'Мін',
    max: 'Макс',
    resetFilters: 'Скинути фільтри',
    showMore: 'більше',
    showLess: 'Показати менше',
    showFilters: 'Показати фільтри',
    loadingFilters: 'Завантаження фільтрів...',

    // Learning Plan
    learningPlan: 'План навчання',
    planEmpty: 'Ваш план порожній.',
    planEmptyHint: 'Додайте методи з каталогу, щоб розпочати.',
    totalDuration: 'Загальна тривалість',
    minutes: 'хвилин',
    method: 'метод',
    methods: 'методи',
    exportPdf: 'Експорт PDF',
    clearPlan: 'Очистити план',

    // Method List
    loadingMethods: 'Завантаження методів...',
    noMethodsFound: 'Не знайдено методів, що відповідають вашим фільтрам.',
    tryAdjustingFilters: 'Спробуйте змінити критерії пошуку.',

    // Method Modal
    loading: 'Завантаження...',
    loadingMethodDetails: 'Завантаження деталей методу...',
    addToPlan: 'Додати до плану',
    downloadPdf: 'Завантажити сценарій',
    downloadMaterials: 'Завантажити вкладення',
    closeModal: 'Закрити вікно',

    // Errors
    error: 'Помилка',

    // InfoCard
    infocard_title: 'Як користуватися цим інструментом',
    infocard_body: 'Переглядайте та фільтруйте методи навчання, а потім додавайте їх до свого плану тренінгу. Експортуйте план у форматі PDF для використання на занятті.',
    infocard_dismiss: 'Зрозуміло',

    // Field Labels
    fieldLabels: {
      title: 'Назва',
      duration: 'Тривалість',
      description: 'Опис',
      module: 'Модуль',
      topics: 'Теми',
      activity_types: 'Типи діяльності',
      contributor: 'Автор',
      learning_goals: 'Цілі навчання',
      preparation: 'Підготовка',
      materials: 'Необхідні матеріали',
      resources: 'Онлайн-ресурси',
      download_materials: 'Завантажити вкладення',
    },
  },

  cs: {
    // Header
    language: 'Jazyk',

    // Filters
    filters: 'Filtry',
    module: 'Modul',
    topic: 'Téma',
    activityType: 'Typ aktivity',
    allModules: 'Všechny moduly',
    allTopics: 'Všechna témata',
    allActivityTypes: 'Všechny typy aktivit',
    duration: 'Délka',
    durationMinutes: 'Délka (minuty)',
    min: 'Min',
    max: 'Max',
    resetFilters: 'Resetovat filtry',
    showMore: 'více',
    showLess: 'Zobrazit méně',
    showFilters: 'Zobrazit filtry',
    loadingFilters: 'Načítání filtrů...',

    // Learning Plan
    learningPlan: 'Plán učení',
    planEmpty: 'Váš plán je prázdný.',
    planEmptyHint: 'Přidejte metody z katalogu a začněte.',
    totalDuration: 'Celková délka',
    minutes: 'minut',
    method: 'metoda',
    methods: 'metody',
    exportPdf: 'Exportovat PDF',
    clearPlan: 'Vymazat plán',

    // Method List
    loadingMethods: 'Načítání metod...',
    noMethodsFound: 'Nebyly nalezeny žádné metody odpovídající vašim filtrům.',
    tryAdjustingFilters: 'Zkuste upravit kritéria vyhledávání.',

    // Method Modal
    loading: 'Načítání...',
    loadingMethodDetails: 'Načítání podrobností metody...',
    addToPlan: 'Přidat do plánu',
    downloadPdf: 'Stáhnout scénář',
    downloadMaterials: 'Stáhnout přílohy',
    closeModal: 'Zavřít okno',

    // Errors
    error: 'Chyba',

    // InfoCard
    infocard_title: 'Jak používat tento nástroj',
    infocard_body: 'Procházejte a filtrujte metody učení, poté je přidejte do svého tréninkového plánu. Exportujte plán jako PDF pro použití na sezení.',
    infocard_dismiss: 'Rozumím',

    // Field Labels
    fieldLabels: {
      title: 'Název',
      duration: 'Délka',
      description: 'Popis',
      module: 'Modul',
      topics: 'Témata',
      activity_types: 'Typy aktivit',
      contributor: 'Přispěvatel',
      learning_goals: 'Cíle učení',
      preparation: 'Příprava',
      materials: 'Potřebné materiály',
      resources: 'Online zdroje',
      download_materials: 'Stáhnout přílohy',
    },
  },

  tr: {
    // Header
    language: 'Dil',

    // Filters
    filters: 'Filtreler',
    module: 'Modül',
    topic: 'Konu',
    activityType: 'Etkinlik türü',
    allModules: 'Tüm modüller',
    allTopics: 'Tüm konular',
    allActivityTypes: 'Tüm etkinlik türleri',
    duration: 'Süre',
    durationMinutes: 'Süre (dakika)',
    min: 'Min',
    max: 'Maks',
    resetFilters: 'Filtreleri sıfırla',
    showMore: 'daha fazla',
    showLess: 'Daha az göster',
    showFilters: 'Filtreleri göster',
    loadingFilters: 'Filtreler yükleniyor...',

    // Learning Plan
    learningPlan: 'Öğrenme Planı',
    planEmpty: 'Planınız boş.',
    planEmptyHint: 'Başlamak için katalogdan yöntemler ekleyin.',
    totalDuration: 'Toplam süre',
    minutes: 'dakika',
    method: 'yöntem',
    methods: 'yöntemler',
    exportPdf: 'PDF dışa aktar',
    clearPlan: 'Planı temizle',

    // Method List
    loadingMethods: 'Yöntemler yükleniyor...',
    noMethodsFound: 'Filtrelerinize uyan yöntem bulunamadı.',
    tryAdjustingFilters: 'Arama kriterlerinizi ayarlamayı deneyin.',

    // Method Modal
    loading: 'Yükleniyor...',
    loadingMethodDetails: 'Yöntem detayları yükleniyor...',
    addToPlan: 'Plana ekle',
    downloadPdf: 'Senaryoyu indir',
    downloadMaterials: 'Ekleri indir',
    closeModal: 'Pencereyi kapat',

    // Errors
    error: 'Hata',

    // InfoCard
    infocard_title: 'Bu araç nasıl kullanılır',
    infocard_body: 'Öğrenme yöntemlerini göz atın ve filtreleyin, ardından eğitim planınıza ekleyin. Planı oturumunuzda kullanmak için PDF olarak dışa aktarın.',
    infocard_dismiss: 'Anladım',

    // Field Labels
    fieldLabels: {
      title: 'Başlık',
      duration: 'Süre',
      description: 'Açıklama',
      module: 'Modül',
      topics: 'Konular',
      activity_types: 'Etkinlik türleri',
      contributor: 'Katkıda bulunan',
      learning_goals: 'Öğrenme hedefleri',
      preparation: 'Hazırlık',
      materials: 'Gerekli malzemeler',
      resources: 'Çevrimiçi kaynaklar',
      download_materials: 'Ekleri indir',
    },
  },

  ar: {
    // Header
    language: 'اللغة',

    // Filters
    filters: 'عوامل التصفية',
    module: 'الوحدة',
    topic: 'الموضوع',
    activityType: 'نوع النشاط',
    allModules: 'جميع الوحدات',
    allTopics: 'جميع المواضيع',
    allActivityTypes: 'جميع أنواع الأنشطة',
    duration: 'المدة',
    durationMinutes: 'المدة (بالدقائق)',
    min: 'الحد الأدنى',
    max: 'الحد الأقصى',
    resetFilters: 'إعادة تعيين عوامل التصفية',
    showMore: 'المزيد',
    showLess: 'عرض أقل',
    showFilters: 'عرض الفلاتر',
    loadingFilters: 'جارٍ تحميل عوامل التصفية...',

    // Learning Plan
    learningPlan: 'خطة التعلم',
    planEmpty: 'خطتك فارغة.',
    planEmptyHint: 'أضف طرقًا من الكتالوج للبدء.',
    totalDuration: 'المدة الإجمالية',
    minutes: 'دقائق',
    method: 'طريقة',
    methods: 'طرق',
    exportPdf: 'تصدير PDF',
    clearPlan: 'مسح الخطة',

    // Method List
    loadingMethods: 'جارٍ تحميل الطرق...',
    noMethodsFound: 'لم يتم العثور على طرق تطابق عوامل التصفية الخاصة بك.',
    tryAdjustingFilters: 'حاول تعديل معايير البحث.',

    // Method Modal
    loading: 'جارٍ التحميل...',
    loadingMethodDetails: 'جارٍ تحميل تفاصيل الطريقة...',
    addToPlan: 'أضف إلى الخطة',
    downloadPdf: 'تنزيل السيناريو',
    downloadMaterials: 'تنزيل المرفقات',
    closeModal: 'إغلاق النافذة',

    // Errors
    error: 'خطأ',

    // InfoCard
    infocard_title: 'كيفية استخدام هذه الأداة',
    infocard_body: 'تصفح أساليب التعلم وقم بتصفيتها، ثم أضفها إلى خطة تدريبك. صدّر الخطة كملف PDF لاستخدامها في جلستك.',
    infocard_dismiss: 'فهمت',

    // Field Labels
    fieldLabels: {
      title: 'العنوان',
      duration: 'المدة',
      description: 'الوصف',
      module: 'الوحدة',
      topics: 'المواضيع',
      activity_types: 'أنواع الأنشطة',
      contributor: 'المساهم',
      learning_goals: 'أهداف التعلم',
      preparation: 'التحضير',
      materials: 'المواد المطلوبة',
      resources: 'الموارد عبر الإنترنت',
      download_materials: 'تنزيل المرفقات',
    },
  },

  nl: {
    // Header
    language: 'Taal',

    // Filters
    filters: 'Filters',
    module: 'Module',
    topic: 'Onderwerp',
    activityType: 'Activiteitstype',
    allModules: 'Alle modules',
    allTopics: 'Alle onderwerpen',
    allActivityTypes: 'Alle activiteitstypen',
    duration: 'Duur',
    durationMinutes: 'Duur (minuten)',
    min: 'Min',
    max: 'Max',
    resetFilters: 'Filters resetten',
    showMore: 'meer',
    showLess: 'Minder tonen',
    showFilters: 'Filters tonen',
    loadingFilters: 'Filters laden...',

    // Learning Plan
    learningPlan: 'Leerplan',
    planEmpty: 'Je plan is leeg.',
    planEmptyHint: 'Voeg methoden toe uit de catalogus om te beginnen.',
    totalDuration: 'Totale duur',
    minutes: 'minuten',
    method: 'methode',
    methods: 'methoden',
    exportPdf: 'PDF exporteren',
    clearPlan: 'Plan wissen',

    // Method List
    loadingMethods: 'Methoden laden...',
    noMethodsFound: 'Geen methoden gevonden die overeenkomen met je filters.',
    tryAdjustingFilters: 'Probeer je zoekcriteria aan te passen.',

    // Method Modal
    loading: 'Laden...',
    loadingMethodDetails: 'Methodedetails laden...',
    addToPlan: 'Toevoegen aan plan',
    downloadPdf: 'Scenario downloaden',
    downloadMaterials: 'Bijlagen downloaden',
    closeModal: 'Venster sluiten',

    // Errors
    error: 'Fout',

    // InfoCard
    infocard_title: 'Hoe dit hulpmiddel te gebruiken',
    infocard_body: 'Blader door en filter leermethoden en voeg ze toe aan je trainingsplan. Exporteer het plan als PDF voor gebruik in je sessie.',
    infocard_dismiss: 'Begrepen',

    // Field Labels
    fieldLabels: {
      title: 'Titel',
      duration: 'Duur',
      description: 'Beschrijving',
      module: 'Module',
      topics: 'Onderwerpen',
      activity_types: 'Activiteitstypen',
      contributor: 'Bijdrager',
      learning_goals: 'Leerdoelen',
      preparation: 'Voorbereiding',
      materials: 'Benodigde materialen',
      resources: 'Online bronnen',
      download_materials: 'Bijlagen downloaden',
    },
  },

  es: {
    // Header
    language: 'Idioma',

    // Filters
    filters: 'Filtros',
    module: 'Módulo',
    topic: 'Tema',
    activityType: 'Tipo de actividad',
    allModules: 'Todos los módulos',
    allTopics: 'Todos los temas',
    allActivityTypes: 'Todos los tipos de actividad',
    duration: 'Duración',
    durationMinutes: 'Duración (minutos)',
    min: 'Mín',
    max: 'Máx',
    resetFilters: 'Restablecer filtros',
    showMore: 'más',
    showLess: 'Mostrar menos',
    showFilters: 'Mostrar filtros',
    loadingFilters: 'Cargando filtros...',

    // Learning Plan
    learningPlan: 'Plan de formación',
    planEmpty: 'Tu plan está vacío.',
    planEmptyHint: 'Añade métodos desde la lista para construir tu plan de formación.',
    totalDuration: 'Duración total',
    minutes: 'minutos',
    method: 'método',
    methods: 'métodos',
    exportPdf: 'Exportar plan',
    clearPlan: 'Limpiar plan',

    // Method List
    loadingMethods: 'Cargando...',
    noMethodsFound: 'No se encontraron métodos',
    tryAdjustingFilters: 'Intenta ajustar tus criterios de búsqueda.',

    // Method Modal
    loading: 'Cargando...',
    loadingMethodDetails: 'Cargando detalles del método...',
    addToPlan: 'Añadir al plan',
    downloadPdf: 'Descargar escenario',
    downloadMaterials: 'Descargar adjuntos',
    closeModal: 'Cerrar',

    // Errors
    error: 'Error',

    // InfoCard
    infocard_title: 'Cómo usar esta herramienta',
    infocard_body: 'Explora y filtra métodos de aprendizaje, luego añádelos a tu plan de formación. Exporta el plan como PDF para usarlo en tu sesión.',
    infocard_dismiss: 'Entendido',

    // Field Labels
    fieldLabels: {
      title: 'Título',
      duration: 'Duración',
      description: 'Descripción',
      module: 'Módulo',
      topics: 'Temas',
      activity_types: 'Tipos de actividad',
      contributor: 'Colaborador',
      learning_goals: 'Objetivos de aprendizaje',
      preparation: 'Preparación',
      materials: 'Materiales necesarios',
      resources: 'Recursos en línea',
      download_materials: 'Descargar adjuntos',
    },
  },

  fr: {
    // Header
    language: 'Langue',

    // Filters
    filters: 'Filtres',
    module: 'Module',
    topic: 'Sujet',
    activityType: "Type d'activité",
    allModules: 'Tous les modules',
    allTopics: 'Tous les sujets',
    allActivityTypes: "Tous les types d'activités",
    duration: 'Durée',
    durationMinutes: 'Durée (minutes)',
    min: 'Min',
    max: 'Max',
    resetFilters: 'Réinitialiser les filtres',
    showMore: 'plus',
    showLess: 'Voir moins',
    showFilters: 'Afficher les filtres',
    loadingFilters: 'Chargement des filtres...',

    // Learning Plan
    learningPlan: "Plan d'apprentissage",
    planEmpty: 'Votre plan est vide.',
    planEmptyHint: 'Ajoutez des méthodes du catalogue pour commencer.',
    totalDuration: 'Durée totale',
    minutes: 'minutes',
    method: 'méthode',
    methods: 'méthodes',
    exportPdf: 'Exporter en PDF',
    clearPlan: 'Vider le plan',

    // Method List
    loadingMethods: 'Chargement des méthodes...',
    noMethodsFound: 'Aucune méthode trouvée correspondant à vos filtres.',
    tryAdjustingFilters: 'Essayez de modifier vos critères de recherche.',

    // Method Modal
    loading: 'Chargement...',
    loadingMethodDetails: 'Chargement des détails de la méthode...',
    addToPlan: 'Ajouter au plan',
    downloadPdf: 'Télécharger le scénario',
    downloadMaterials: 'Télécharger les pièces jointes',
    closeModal: 'Fermer',

    // Errors
    error: 'Erreur',

    // InfoCard
    infocard_title: 'Comment utiliser cet outil',
    infocard_body: "Parcourez et filtrez les méthodes d'apprentissage, puis ajoutez-les à votre plan de formation. Exportez le plan en PDF pour l'utiliser lors de votre session.",
    infocard_dismiss: 'Compris',

    // Field Labels
    fieldLabels: {
      title: 'Titre',
      duration: 'Durée',
      description: 'Description',
      module: 'Module',
      topics: 'Sujets',
      activity_types: "Types d'activités",
      contributor: 'Contributeur',
      learning_goals: "Objectifs d'apprentissage",
      preparation: 'Préparation',
      materials: 'Matériaux nécessaires',
      resources: 'Ressources en ligne',
      download_materials: 'Télécharger les pièces jointes',
    },
  },
};

/**
 * Language Context Type
 */
interface LanguageContextType {
  /** Current language code */
  language: LanguageCode;
  /** Set current language */
  setLanguage: (lang: LanguageCode) => void;
  /** Translation function - returns translated string for key */
  t: (key: keyof UITranslations) => string;
  /** Field labels for current language */
  fieldLabels: FieldLabels;
  /** All available translations for current language */
  translations: Translations;
}

const STORAGE_KEY = 'gf_language';
const DEFAULT_LANGUAGE: LanguageCode = 'en';

/**
 * Get initial language from localStorage, URL path, WordPress settings, or browser
 */
function getInitialLanguage(): LanguageCode {
  if (typeof window !== 'undefined') {
    // 1. localStorage takes highest priority (explicit user choice)
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && isValidLanguage(stored)) {
        return stored as LanguageCode;
      }
    } catch (e) {
      // localStorage not available
    }

    // 2. Detect from URL path (e.g. /pl/app/, /de/app/)
    // This handles Polylang directory-based language URLs reliably, regardless
    // of whether the WordPress app page has a Polylang translation assigned.
    const pathSegment = window.location.pathname.split('/').find(s => s.length > 0);
    if (pathSegment && isValidLanguage(pathSegment)) {
      return pathSegment as LanguageCode;
    }

    // 3. WordPress Polylang setting (pll_current_language())
    if (window.tpSettings?.currentLang && isValidLanguage(window.tpSettings.currentLang)) {
      return window.tpSettings.currentLang as LanguageCode;
    }

    // 4. Browser language
    const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
    const supported = Object.keys(translations);
    if (browserLang && supported.includes(browserLang)) {
      return browserLang as LanguageCode;
    }
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Validate if a string is a valid language code.
 * Derives the supported list from tpSettings.languages at runtime when available,
 * falling back to the compile-time LanguageCode union values.
 */
function isValidLanguage(lang: string): lang is LanguageCode {
  const supported = window.tpSettings?.languages?.map((l: { code: string }) => l.code)
    ?? ['en', 'de', 'pl', 'da', 'uk', 'cs', 'tr', 'ar', 'nl', 'es', 'fr'];
  return supported.includes(lang);
}

/**
 * Language Context
 */
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Language Provider Props
 */
interface LanguageProviderProps {
  children: React.ReactNode;
  /** Optional initial language override */
  initialLanguage?: LanguageCode;
}

/**
 * Language Provider Component
 *
 * Wrap your app with this provider to enable translations:
 *
 * @example
 * ```tsx
 * <LanguageProvider>
 *   <App />
 * </LanguageProvider>
 * ```
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  initialLanguage,
}) => {
  const [language, setLanguageState] = useState<LanguageCode>(
    initialLanguage || getInitialLanguage()
  );

  // Persist language to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch (e) {
      console.error('Failed to save language preference:', e);
    }
  }, [language]);

  // Set language with validation
  const setLanguage = useCallback((lang: LanguageCode): void => {
    if (isValidLanguage(lang)) {
      setLanguageState(lang);
    } else {
      console.warn(`Invalid language code: ${lang}. Falling back to 'en'.`);
      setLanguageState('en');
    }
  }, []);

  // Current translations — check window.tpI18n first, then embedded object, then English fallback
  const currentTranslations = useMemo(
    () => (window.tpI18n?.[language] as unknown as Translations) ?? translations[language] ?? translations.en,
    [language]
  );

  // Translation function
  const t = useCallback(
    (key: keyof UITranslations): string => {
      return currentTranslations[key] || translations.en[key] || key;
    },
    [currentTranslations]
  );

  // Field labels for current language
  const fieldLabels = useMemo(
    () => currentTranslations.fieldLabels,
    [currentTranslations]
  );

  const contextValue = useMemo<LanguageContextType>(
    () => ({
      language,
      setLanguage,
      t,
      fieldLabels,
      translations: currentTranslations,
    }),
    [language, setLanguage, t, fieldLabels, currentTranslations]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * useLanguage Hook
 *
 * Access the language context from any component.
 *
 * @example
 * ```tsx
 * const { language, setLanguage, t, fieldLabels } = useLanguage();
 *
 * return (
 *   <div>
 *     <h2>{t('learningPlan')}</h2>
 *     <button onClick={() => setLanguage('de')}>Switch to German</button>
 *   </div>
 * );
 * ```
 */
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);

  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};

/**
 * Export translations for testing
 */
export { translations };

/**
 * Export type for language codes
 */
export type { LanguageCode };
